<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('order.offersAndOrders') }}</span>
        <h3 class="page-title">
          {{ $t('ui.order') }}
        </h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          class="mr-2"
          :disabled="taxNumberCheckInProgress"
          @click.prevent="$router.push(backUrl)">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-dropdown
          v-if="!userIsCustomer && !userIsWarehouseman && canSendSettlementInvoice && !invoiceRequestInProgress"
          :disabled="settlementInvoiceInProgress !== false || taxNumberCheckInProgress"
          :text="$t('order.requestInvoice')"
          theme="warning"
          class="mr-2"
          key="request-invoice-button">
          <d-dropdown-item>
            <a
              @click.prevent="checkCustomerTaxNumberBeforeInvoice(false)">
              <template v-if="!settlementInvoiceInProgress && !taxNumberCheckInProgress">{{ $t('order.requestInvoice') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
          <d-dropdown-item v-if="userIsAdmin">
            <a
              @click.prevent="checkCustomerTaxNumberBeforeInvoice(true)">
              <template v-if="!settlementInvoiceInProgress && !taxNumberCheckInProgress">{{ $t('order.requestInvoice') }} ({{ $t('ui.immediate') }})</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
        </d-dropdown>

        <d-button
          v-if="!userIsWarehouseman && invoiceRequestInProgress"
          :disabled="true"
          theme="warning"
          :outline="true"
          class="mr-2"
          v-pure-html="$t('order.invoiceRequestSent')"
          key="request-sent-button">
        </d-button>

        <d-button
          v-if="!invoiceCancelRequestSent && !userIsWarehouseman && (invoiceRequestInProgress || canResetInvoiceRequest)"
          :disabled="invoiceResetInProgress"
          theme="danger"
          :outline="true"
          class="mr-2"
          @click.prevent="resetInvoiceRequest"
          v-pure-html="invoiceResetInProgress ? spinnerTemplate : $t('order.invoiceRequestCancel')">
        </d-button>

        <d-dropdown
          v-if="!userIsWarehouseman && !userIsCustomer"
          :text="$t('order.download')"
          theme="info"
          class="mr-2">
          <d-dropdown-item>
            <a
              :disabled="downloadingPdf !== false"
              @click.prevent="downloadPdf()"
              :style="'display: block;' + ((downloadingPdf !== false) ? 'pointer-events: none;' : '')">
              <template v-if="!downloadingPdf">{{ $t('ui.pdf') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
          <d-dropdown-item>
            <a
              :disabled="downloadingXlsx !== false"
              @click.prevent="downloadXlsx()"
              :style="'display: block;' + ((downloadingXlsx !== false) ? 'pointer-events: none;' : '')">
              <template v-if="!downloadingXlsx">{{ $t('ui.xlsx') }}</template>
              <template v-else>
                <div class="spinner-border spinner-border-sm" role="status"></div>
              </template>
            </a>
          </d-dropdown-item>
        </d-dropdown>
      </d-col>
    </d-row>

    <d-alert
      v-if="!userIsCustomer && orderData && orderData.invoice_country_prefix !== 'PL' && orderData.customer && !orderData.customer.last_correct_vies_verification_at && invoiceCountryPrefixIsVerified"
      show
      theme="danger">
      {{ $t('order.orderWontBeSentToErpDueLackOfVerifiedVIES') }}
    </d-alert>

    <d-alert
      v-if="!userIsWarehouseman && !isLocked && orderData.editing_by && orderData.editing_by !== $store.state.user.id"
      show
      theme="danger"
      class="d-flex align-middle">
      <div>
        {{ $t('order.blockedOrderDifferentUser', { user: orderData.edit_by.name }) }}
      </div>

      <d-button
        theme="light"
        class="ml-auto"
        @click.prevent="unblockOrder">
        {{ $t('order.unblockOrder') }}
      </d-button>
    </d-alert>

    <d-alert
      v-if="!userIsWarehouseman && !userIsCustomer && orderData && orderData.incorrect_import"
      show
      theme="danger">
      {{ $t('softlab.orderImportToSoftlabError') }}<template v-if="orderData && orderData.incorrect_import_at"> ({{ orderData.incorrect_import_at }})</template>.
      <template v-if="orderData && orderData.incorrect_import_description">
        {{ $t('softlab.errorDescription') }}: {{ orderData.incorrect_import_description }}
      </template>
    </d-alert>

    <d-alert
      v-if="!userIsWarehouseman && !userIsCustomer && orderData && orderData.collect_alert"
      :show="showCollectAlert"
      theme="warning"
      class="d-flex">
      <div>{{ $t('order.orderCollectedPartiallyOutOfStock') }}</div>
      <i
        class="material-icons ml-auto"
        style="cursor: pointer"
        @click="dismissCollectAlert">close</i>
    </d-alert>

    <d-alert
      :show="showEmbroideryAlert"
      theme="danger"
      class="d-flex">
      {{ $t('order.warehouse.boxes.info.embroideryAlert') }}
    </d-alert>

    <d-alert
      v-if="!userIsWarehouseman && !userIsCustomer && orderData && orderData.collect_warning"
      theme="warning"
      :show="true"
      class="d-flex">
      <div>{{ $t('order.collectedProductsCountDifferentThanOrdered') }}</div>
    </d-alert>

    <!-- Form -->
    <d-form>
      <validation-observer ref="order-form" tag="form">
        <d-card class="mb-5">
          <d-tabs v-sticky-tabs
            v-if="!loadError"
            card>
            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <d-alert
                v-if="orderData.is_special_requirement"
                :show="true"
                theme="danger"
                class="pt-4 pb-4 has-special-requirements">
                <i class="material-icons">warning</i>
                {{ $t('order.hasSpecialRequirementsWarning') }}
              </d-alert>

              <d-alert
                v-if="orderData.tracking_number && (!invoiceRequestInProgress && noInvoiceRequests && status !== 'SENT')"
                :show="true"
                theme="warning"
                class="pt-4 pb-4 has-special-requirements">
                <i class="material-icons">warning</i>
                {{ $t('order.createInvoiceOrChangeToSent') }}
              </d-alert>

              <d-alert
                v-if="userIsAdmin && !orderData.can_update"
                :show="true"
                theme="light"
                class="pt-4 pb-4 is-unlock-notice">
                {{ $t('order.unlockOrderNotice') }}
                <d-button
                  size="sm"
                  class="ml-2"
                  theme="dark"
                  :outline="true"
                  @click.prevent="unlockOrderEdit">
                  <div
                    v-if="unlockInProgress"
                    class="spinner-border spinner-border-sm" role="status"></div>
                  {{ $t('order.unlockOrder') }}
                </d-button>
              </d-alert>

              <h4 class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('ui.generalInfo') }}

                <div class="ml-auto order-status-wrapper">
                  <span
                    v-if="userIsCustomer || userIsWarehouseman"
                    class="text-primary text-right d-block">
                    {{ orderStatusText }}
                  </span>

                  <order-status
                    v-if="!userIsCustomer && !userIsWarehouseman"
                    :isLocked="isLocked && !userIsAdmin"
                    :initialStatus="status"
                    :settlementsNumber="orderData.settlements.length" />
                </div>
              </h4>

              <d-row>
                <d-col cols="12">
                  <span
                    v-if="orderData.exclude_from_erp_integration"
                    class="text-danger">
                    {{ $t('order.erp.isExcluded') }}
                  </span>

                  <d-badge
                    v-if="!orderData.is_virtual && !orderData.exclude_from_erp_integration"
                    :theme="orderData.erp_optima_updated_at ? 'success' : 'danger'"
                    class="mr-3"
                    :outline="true"
                    size="lg"
                    @click.native="openErpIntegrationPopup"
                    style="cursor: pointer; font-size: 18px;">
                    <template v-if="orderData.erp_optima_updated_at">
                      {{ $t('order.erp.integratedWithErp') }}
                    </template>
                    <template v-else>
                      {{ $t('order.erp.notIntegratedWithErp') }}
                    </template>
                  </d-badge>

                  <d-button
                    v-if="!orderData.exclude_from_erp_integration && userIsAdmin"
                    theme="danger"
                    @click.prevent="excludeFromERP">
                    <div
                      v-if="excludeFromErpInProgress"
                      class="spinner-border spinner-border-sm" role="status"></div>
                    {{ $t('order.erp.excludeFrom') }}
                  </d-button>

                  <d-button
                    v-if="orderData.exclude_from_erp_integration && userIsAdmin"
                    theme="success"
                    class="ml-3"
                    @click.prevent="includeToERP">
                    <div
                      v-if="includeToErpInProgress"
                      class="spinner-border spinner-border-sm" role="status"></div>
                    {{ $t('order.erp.includeTo') }}
                  </d-button>
                </d-col>
              </d-row>

              <hr>

              <d-row v-if="isIndentList && orderData.container_id && orderData.indent_container">
                <d-col cols="12">
                  <label>
                    {{ $t('container.container') }}:
                  </label>
                  <span class="text-primary">
                    {{ orderData.indent_container.number }} ({{ orderData.indent_container.date }})
                  </span>
                </d-col>
              </d-row>

              <d-row v-if="realizationDate !== $t('ui.lack')">
                <d-col cols="12">
                  <label>{{ $t('order.preferedByClientExecutionDate') }}:</label> <span class="text-primary">{{ realizationDate }}</span>
                </d-col>
              </d-row>

              <hr v-if="realizationDate !== $t('ui.lack')">

              <d-row>
                <d-col
                  v-if="!userIsCustomer"
                  cols="3" md="3" lg="3">
                  <label>
                    {{ $t('ui.orderName') }}
                  </label>

                  <div>
                    {{ orderData.order_name }}

                    <template v-if="orderData.order_name_notes">
                      <br>
                      <small>{{ orderData.order_name_notes }}</small>
                    </template>
                  </div>
                </d-col>

                <d-col
                  v-if="!userIsCustomer"
                  cols="3" md="3" lg="3">
                  <label>
                    {{ $t('order.assingedWorker') }}
                  </label>

                  <div>
                    {{ orderData.user_name }}
                  </div>
                </d-col>

                <d-col
                  v-if="!userIsCustomer"
                  cols="2" md="2" lg="2">
                  <label>
                    {{ $t('order.transactionType') }}
                  </label>

                  <div v-if="orderData.transaction_type === 'DOMESTIC'">
                    {{ $t('order.transactionTypes.domestic') }}
                  </div>
                  <div v-else-if="orderData.transaction_type === 'UE'">
                    {{ $t('order.transactionTypes.eu') }}
                  </div>
                  <div v-else-if="orderData.transaction_type === 'EXPORT'">
                    {{ $t('order.transactionTypes.export') }}
                  </div>
                  <div v-else-if="orderData.transaction_type === 'NO_EU_VAT'">
                    {{ $t('order.transactionTypes.noVatEU') }}
                  </div>
                  <div v-else>
                    {{ $t('ui.lack') }}
                  </div>

                  <label class="mt-3">
                    {{ $t('order.customTransactionType') }}
                  </label>

                  <div>
                    <template v-if="orderData.custom_transaction_type">{{ $t('ui.yes') }}</template>
                    <template v-else>{{ $t('ui.no') }}</template>
                  </div>
                </d-col>

                <d-col :cols="!userIsCustomer ? '2' : '6'">
                  <label>
                    <span
                      v-if="hasDeadlineWarning()"
                      class="text-danger">
                      {{ $t('ui.deadlineCap') }} <i class="material-icons">warning</i>
                    </span>
                    <span v-else class="text-danger">
                      {{ $t('ui.deadlineCap') }}
                    </span>
                  </label>

                  <div v-if="deadline" class="text-danger">
                    {{ deadline }}
                  </div>
                  <div v-else class="text-danger">
                    {{ $t('ui.lack') }}
                  </div>
                </d-col>

                <d-col :cols="!userIsCustomer ? '2' : '6'">
                  <label>
                    {{ $t('order.estimatedShippmentDate') }}
                  </label>

                  <div v-if="shipperDate">
                    {{ shipperDate }}
                  </div>
                  <div v-else>
                    {{ $t('ui.lack') }}
                  </div>
                </d-col>
              </d-row>

              <hr>

              <d-row v-if="!userIsCustomer">
                <d-col cols="3" md="3" lg="3">
                  <label>
                    {{ $t('order.isVirtual') }}:
                  </label>

                  <div>
                    <template v-if="orderData.is_virtual">{{ $t('ui.yes') }}</template>
                    <template v-else>{{ $t('ui.no') }}</template>
                  </div>
                </d-col>

                <d-col cols="3" md="3" lg="3">
                  <label>
                    {{ $t('order.requiresAdvance') }}:
                  </label>

                  <div>
                    <template v-if="orderData.is_advance">{{ $t('ui.yes') }}</template>
                    <template v-else>{{ $t('ui.no') }}</template>
                  </div>
                </d-col>

                <d-col
                  v-if="orderData.is_advance"
                  cols="3" md="3" lg="3">
                  <label>
                    {{ $t('order.amountOfAdvance') }}
                  </label>

                  <div>
                    {{ orderData.advance_percent }} %
                  </div>
                </d-col>
              </d-row>

              <hr v-if="!userIsCustomer && orderData.is_special_requirement">

              <d-row v-if="!userIsCustomer && orderData.is_special_requirement">
                <d-col cols="12" md="12" lg="12">
                  <label>{{ $t('order.isSpecialRequirement') }}:</label>

                  <pre class="px-2 py-2">{{ orderData.special_requirement }}</pre>
                </d-col>
              </d-row>

              <hr v-if="!userIsCustomer">

              <d-row>
                <d-col cols="12" md="6" lg="6">
                  <h5>{{ $t('users.customer') }}:</h5>

                  <div
                    v-if="orderData.customer"
                    class="text-muted">
                    {{ orderData.customer.name }}<br>
                    {{ orderData.customer.email }}<br>
                    {{ orderData.customer.phone }}<br>
                  </div>

                  <h6 class="mt-2">{{ $t('order.addressOfCorrespondence') }}:</h6>

                  <div
                    v-if="orderData.customer"
                    class="text-muted">
                    <template v-if="orderData.customer.correspondence_name">{{ orderData.customer.correspondence_name }}<br></template>
                    {{ orderData.customer.correspondence_company_name }}<br>
                    {{ orderData.customer.correspondence_postcode }} {{ orderData.customer.correspondence_city }}<br>
                    {{ orderData.customer.correspondence_street }} <template v-if="orderData.customer.correspondence_building">{{ orderData.customer.correspondence_building }}<template v-if="orderData.customer.correspondence_locale"> / {{ orderData.customer.correspondence_locale }}</template></template><br>
                  </div>

                  <h6 class="mt-2">{{ $t('customer.invoiceInfo') }}:</h6>

                  <div class="text-muted">
                    {{ orderData.invoice_name }}<br>
                    {{ orderData.invoice_country_prefix }}{{ orderData.invoice_tax_number }}<br>
                    {{ orderData.invoice_postcode }} {{ orderData.invoice_city }}<br>
                    {{ orderData.invoice_street }} <template v-if="orderData.invoice_building">{{ orderData.invoice_building }}<template v-if="orderData.invoice_locale"> / {{ orderData.invoice_locale }}</template></template><br>
                  </div>

                  <h5 class="mt-4">{{ $t('order.clientContactPerson') }}:</h5>

                  <div class="text-muted">
                    {{ orderData.contact_name }}<br>
                    {{ orderData.contact_email }}<br>
                    {{ orderData.contact_phone }}<br>
                  </div>

                  <h5 class="mt-4">{{ $t('order.shippingAddress') }}:</h5>

                  <div class="text-muted">
                    {{ orderData.address_name }}<br>
                    {{ orderData.address_company_name }}<br>
                    {{ orderData.address_phone }}<br>
                    {{ orderData.address_country }}<br>
                    {{ orderData.address_postcode }} {{ orderData.address_city }}<br>
                    {{ orderData.address_street }} <template v-if="orderData.address_building">{{ orderData.address_building }}<template v-if="orderData.address_locale"> / {{ orderData.address_locale }}</template></template><br>
                    <template v-if="orderData.address_line_2">{{ orderData.address_line_2 }}<br></template>
                    <template v-if="orderData.address_line_3">{{ orderData.address_line_3 }}<br></template>
                  </div>

                  <div class="text-muted mt-2">
                    <strong>{{ $t('order.addressConfirmed') }}:</strong>
                    <span
                      v-if="orderData.address_confirmed"
                      class="text-success">
                      {{ $t('ui.yes') }}
                    </span>
                    <span
                      v-else
                      class="text-danger">
                      {{ $t('ui.no') }}
                    </span>

                    <template v-if="orderData.address_confirmed">
                      <template v-if="orderData.address_confirmed_at">
                        <br>{{ $t('order.addressConfirmedAt') }}: {{ orderData.address_confirmed_at }}
                      </template>

                      <template v-if="addressConfirmedByLabel">
                        <br>{{ $t('order.addressConfirmedBy') }}: {{ addressConfirmedByLabel }}
                      </template>
                    </template>
                  </div>

                  <h5 v-if="!userIsCustomer" class="mt-4">{{ $t('order.deliveryNote') }}:</h5>

                  <p
                    v-if="!userIsCustomer && !orderData.delivery_note_file"
                    class="text-muted">
                    {{ $t('attachment.noAttachment') }}
                  </p>

                  <p>
                    <a
                      v-if="!userIsCustomer && orderData.delivery_note_file"
                      href="#"
                      @click.prevent="downloadDeliveryNote()">
                      <template v-if="!downloadingDeliveryNote"><i class="material-icons">description</i>{{ $t('attachment.downloadAttachment') }}</template>
                      <template v-if="downloadingDeliveryNote">{{ $t('ui.downloadInProgress') }}&hellip;</template>
                    </a>
                  </p>

                  <h5 v-if="!userIsCustomer" class="mt-4">
                    {{ $t('order.dataOnInvoice') }}:
                    <small v-if="!editDescription && !userIsWarehouseman">
                      <a
                        href="#"
                        @click.prevent="toggleDescriptionEdit">
                        <i class="material-icons">edit</i>
                        {{ $t('ui.edit') }}
                      </a>
                    </small>
                  </h5>

                  <d-textarea
                    v-if="!userIsCustomer && !userIsWarehouseman && editDescription"
                    v-model="descriptionToEdit"
                    :value.prop="descriptionToEdit"
                    :rows="5"></d-textarea>

                  <d-button
                    v-if="!userIsCustomer && editDescription"
                    theme="success"
                    size="sm"
                    class="mt-2 mb-3"
                    :disabled="saveDescriptionInProgress"
                    @click.prevent="saveEditedDescription">
                    <div
                      v-if="saveDescriptionInProgress"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <template v-else>
                      {{ $t('ui.save') }}
                    </template>
                  </d-button>

                  <d-button
                    v-if="!userIsCustomer && editDescription"
                    theme="danger"
                    size="sm"
                    class="mt-2 ml-2 mb-3"
                    :disabled="saveDescriptionInProgress"
                    @click.prevent="editDescription = false">
                    {{ $t('ui.cancel') }}
                  </d-button>

                  <p v-if="!userIsCustomer && orderData.description !== '' && !editDescription" class="text-muted">
                    {{ orderData.description }}
                  </p>

                  <p v-else-if="!userIsCustomer && !editDescription" class="text-muted">
                    {{ $t('ui.lack') }}
                  </p>
                </d-col>

                <d-col cols="12" md="6" lg="6">
                  <h5>{{ $t('order.specialConfiguration') }}:</h5>

                  <table class="mb-4 text-muted">
                    <tr>
                      <td><strong>{{ $t('customer.discount.includeCustomerDiscounts') }}:</strong></td>
                      <td>
                        <span v-if="orderData.include_discounts" class="text-success">{{ $t('ui.yes') }}</span>
                        <span v-else class="text-danger">{{ $t('ui.no') }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('price.includeSetupPrices') }}:</strong></td>
                      <td>
                        <span v-if="orderData.include_setup_prices" class="text-success">{{ $t('ui.yes') }}</span>
                        <span v-else class="text-danger">{{ $t('ui.no') }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.freeDelivery') }}:</strong></td>
                      <td>
                        <span v-if="orderData.free_freight" class="text-success">{{ $t('ui.yes') }}</span>
                        <span v-else class="text-danger">{{ $t('ui.no') }}</span>
                      </td>
                    </tr>
                  </table>

                  <h5>{{ $t('order.additionalInfo') }}:</h5>

                  <table class="mb-4 text-muted">
                    <tr>
                      <td><strong>{{ $t('customer.discount.productDiscount') }}:</strong></td>
                      <td>{{ orderData.discount_products }}%</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('customer.discount.serviceDiscount') }}:</strong></td>
                      <td>{{ orderData.discount_services }}%</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('customer.discount.shippingDiscount') }}:</strong></td>
                      <td>{{ orderData.discount_shipping }}%</td>
                    </tr>
                    <tr v-if="orderData.skonto">
                      <td><strong>Skonto:</strong></td>
                      <td>{{ orderData.skonto }} {{ orderData.currency_code }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.brand.brand') }}:</strong></td>
                      <td>{{ orderData.brand }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.orderCurrency') }}:</strong></td>
                      <td>{{ orderData.currency_code }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.offerLanguage') }}:</strong></td>
                      <td>{{ orderData.language_code }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.executionTime') }}:</strong></td>
                      <td>{{ orderData.processing_time_name }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('ui.shipper') }}:</strong></td>
                      <td>{{ orderData.shipper_name }}</td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.shippingCost') }}:</strong></td>
                      <td>
                        <template v-if="orderData.shipper_price === orderData.shipper_price_before_discount">
                          {{ Number(orderData.shipper_price_before_discount).toFixed(2) }} {{ orderData.currency_code }}
                        </template>
                        <template v-else>
                          {{ Number(orderData.shipper_price).toFixed(2) }} {{ orderData.currency_code }}
                          <small><del>{{ Number(orderData.shipper_price_before_discount).toFixed(2) }} {{ orderData.currency_code }}</del></small>
                        </template>
                        <br>
                        <small>
                          {{ $t('order.shippingCostCustom') }}:
                          <span v-if="orderData.custom_shipping_cost" class="text-success">{{ $t('ui.yes') }}</span>
                          <span v-else class="text-danger">{{ $t('ui.no') }}</span>
                        </small>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.additionalShippingCost') }}:</strong></td>
                      <td>
                        <template v-if="orderData.shipper_additional_price === orderData.shipper_additional_price_before_discount">
                          {{ Number(orderData.shipper_additional_price).toFixed(2) }} {{ orderData.currency_code }}
                        </template>
                        <template v-else>
                          {{ Number(orderData.shipper_additional_price).toFixed(2) }} {{ orderData.currency_code }}
                          <small><del>{{ Number(orderData.shipper_additional_price_before_discount).toFixed(2) }} {{ orderData.currency_code }}</del></small>
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.trackingNumber') }}:</strong></td>
                      <td>
                        <template v-if="orderData.tracking_number">
                          {{ orderData.tracking_number }}
                        </template>
                        <template v-else>
                          {{ $t('ui.lack') }}
                        </template>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>{{ $t('order.cartonsNumber') }}:</strong></td>
                      <td>{{ orderData.shipper_cartons_number }}</td>
                    </tr>
                  </table>

                  <!--
                  <d-button
                    v-if="canDownloadShipperNote"
                    theme="primary"
                    class="mr-2"
                    @click.prevent="getShipperNote">
                    Pobierz list przewozowy
                  </d-button>
                  -->

                  <br><br>

                  <h5 v-if="!userIsCustomer && isIndentList">{{ $t('order.indent.warehouse') }}:</h5>

                  <p>
                    {{ orderData.warehouse }}
                  </p>

                  <h5 v-if="orderData.customer_description">{{ $t('order.commentsForCustomer') }}:</h5>

                  <p v-if="orderData.customer_description" class="text-muted">
                    {{ orderData.customer_description }}
                  </p>

                  <h5 v-if="!userIsCustomer">{{ $t('order.additionalInfoInternal') }}:</h5>

                  <p v-if="!userIsCustomer && orderData.internal_description !== ''" class="text-muted">
                    {{ orderData.internal_description }}
                  </p>

                  <p v-else-if="!userIsCustomer" class="text-muted">
                    {{ $t('ui.lack') }}
                  </p>

                  <h5 v-if="!userIsCustomer">{{ $t('order.warehouse.commentsForWarehouse') }}:</h5>

                  <pre v-if="!userIsCustomer && orderData.description_for_warehouse">{{ orderData.description_for_warehouse }}</pre>

                  <p v-else-if="!userIsCustomer" class="text-muted">
                    {{ $t('ui.lack') }}
                  </p>

                  <h5 v-if="!userIsCustomer">
                    {{ $t('order.commentsFromWarehouse') }}:

                    <small v-if="!editWarehouseDescription && userIsWarehouseman">
                      <a
                        href="#"
                        @click.prevent="toggleWarehouseDescriptionEdit">
                        <i class="material-icons">edit</i>
                        {{ $t('ui.edit') }}
                      </a>
                    </small>
                  </h5>

                  <pre v-if="!userIsCustomer && orderData && orderData.warehouse_description && !editWarehouseDescription">{{ orderData.warehouse_description }}</pre>

                  <p v-else-if="!userIsCustomer && !editWarehouseDescription" class="text-muted">
                    {{ $t('ui.lack') }}
                  </p>

                  <d-textarea
                    v-if="!userIsCustomer && userIsWarehouseman && editWarehouseDescription"
                    v-model="warehouseDescriptionToEdit"
                    :value.prop="warehouseDescriptionToEdit"
                    :rows="5"></d-textarea>

                  <d-button
                    v-if="!userIsCustomer && editWarehouseDescription"
                    theme="success"
                    size="sm"
                    class="mt-2 mb-3"
                    :disabled="saveWarehouseDescriptionInProgress"
                    @click.prevent="saveEditedWarehouseDescription">
                    <div
                      v-if="saveWarehouseDescriptionInProgress"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    <template v-else>
                      {{ $t('ui.save') }}
                    </template>
                  </d-button>

                  <d-button
                    v-if="!userIsCustomer && userIsWarehouseman && editWarehouseDescription"
                    theme="danger"
                    size="sm"
                    class="mt-2 ml-2 mb-3"
                    :disabled="saveWarehouseDescriptionInProgress"
                    @click.prevent="editWarehouseDescription = false">
                    {{ $t('ui.cancel') }}
                  </d-button>

                  <template v-if="orderData.transaction_type === 'EXPORT'">
                    <br><br>

                    <order-ie-files
                      :orderID="orderData ? orderData.id : 0"
                      :orderFiles="orderData && orderData.ie_files ? orderData.ie_files : []" />
                  </template>

                  <br><br>

                  <order-files
                    :orderID="orderData ? orderData.id : 0"
                    :orderFiles="orderData ? orderData.files : []"
                    :readOnly="true" />

                  <br><br>

                  <h5>{{ $t('order.connectedOrders') }}:</h5>

                  <ul v-if="orderData.connected.length" class="ml-0 pl-3">
                    <li
                      v-for="(connectedOrder, index) of orderData.connected"
                      :key="'connected-order-' + index">
                      <small>{{ connectedOrder.order_name }} (#{{ connectedOrder.id }})</small>
                    </li>
                  </ul>

                  <p v-else>
                    <small>{{ $t('order.noConnectedOrders') }}&hellip;</small>
                  </p>

                  <div
                    v-if="!userIsCustomer"
                    class="form-group">
                    <label>
                      {{ $t('settings.orderTags.orderTags') }}:
                    </label>

                    <d-alert
                      v-if="!orderData.tags.length"
                      show
                      theme="light">
                      {{ $t('ui.lack') }}
                    </d-alert>

                    <ul
                      v-if="orderData.tags.length"
                      class="order-tags-list">
                      <li
                        v-for="(tag, index) of orderData.tags"
                        :key="'order-tag-' + index">
                        <d-badge :style="'background-color:' + tag.color + '; color: ' + contrastColor(tag.color) + ';'">
                          {{ tag.name }}
                        </d-badge>
                      </li>
                    </ul>
                  </div>
                </d-col>
              </d-row>
            </d-tab>

            <!-- Zapytanie -->
            <d-tab
              v-if="orderData.is_inquiry"
              :title="$t('ui.inquiry')">
              <request-creator
                ref="request-creator"
                :readOnly="true"
                :products="products"
                :currencyID="allDataLoaded ? orderData.currency_id : 0"
                :currencies="currencies"
                :services="services"
                :submittedFiles="orderData.files"
                :servicesGroups="servicesGroups"
                :tagCategories="tagCategories" />
            </d-tab>

            <!-- Zamówienie -->
            <d-tab :title="userIsCustomer && isOffer ? $t('ui.offer') : $t('ui.order')">
              <fieldset
                v-if="orderData.customer_order_description"
                class="my-0">
                <legend>
                  <small><small><strong>{{ $t('order.customerOrderDescription') }}:</strong></small></small>
                </legend>
                <p class="my-0">
                  {{ orderData.customer_order_description }}
                </p>
              </fieldset>

              <fieldset
                v-if="orderData.customer_order_number"
                class="mb-5">
                <legend>
                  <small><small><strong>{{ $t('order.customerOrderNumber') }}:</strong></small></small>
                </legend>
                <p class="text-primary my-0">
                  {{ orderData.customer_order_number }}
                </p>
              </fieldset>

              <order-creator
                ref="order-creator"
                :title="$t('ui.order')"
                :titleLabel="$t('order.orderDative')"
                :readOnly="true"
                :products="products"
                :indentProducts="indentProducts"
                :services="services"
                :currencyId="orderData.currency_id"
                :selectedCurrencySymbol="orderData.currency_code"
                :shipperPrice="orderData.shipper_price"
                :shipperAdditionalPrice="orderData.shipper_additional_price"
                :includeSetupPrice="orderData.include_setup_prices"
                :customerProductsDiscount="orderData.include_discounts ? prepareDiscount(orderData.discount_products) : 0"
                :customerServicesDiscount="orderData.include_discounts ? prepareDiscount(orderData.discount_services) : 0"
                :status="status"
                :orderSetUpInProgress="orderSetUpInProgress"
                :warnings="orderData && orderData.warnings ? orderData.warnings : []"
                :collectAlert="orderData && !!orderData.collect_alert"
                :collectWarning="orderData && !!orderData.collect_warning"
                :deadline="orderData.deadline"
                :isIndent="isIndentList"
                :orderPurchases="orderData && orderData.purchases"
                :isOffer="isOffer"
                :skonto="orderData.skonto"
                :servicesGroups="servicesGroups"
                :allPackagesData="orderData && orderData.all_order_packages" />
            </d-tab>

            <!-- Kontakty z klientem -->
            <d-tab
              v-if="!userIsWarehouseman && !userIsCustomer"
              :title="$t('customer.contactsWithCustomer')">
              <order-contacts
                :orderID="parseInt(this.$route.params.id, 10)"
                :isOrderView="true" />
            </d-tab>

            <!-- Reklamacje -->
            <d-tab
              v-if="!userIsWarehouseman && !userIsCustomer"
              :title="$t('complaints.complaints')">
              <order-complaints
                :orderID="parseInt(this.$route.params.id, 10)"
                :isOrderView="true" />
            </d-tab>

            <!-- Przesyłki -->
            <d-tab
              v-if="!userIsWarehouseman && (userIsAdminOrWorker || userIsCustomer) && $showUpsIntegration"
              :title="$t('shipments.shipments')">
              <order-shipments
                :orderID="parseInt(this.$route.params.id, 10)"
                :orderName="orderData.order_name"
                :addressData="shipmentAddressData"
                :customerID="orderData.customer_id"
                :contactData="shipmentContactData"
                :addressConfirmed="!!orderData.address_confirmed" />
            </d-tab>

            <!-- PUESC -->
            <d-tab
              v-if="userIsAdminOrWorker"
              :title="$t('order.puesc.title')">
              <order-puesc
                :orderID="parseInt(this.$route.params.id, 10)"
                :addressData="shipmentAddressData"
                :countries="countries"
                :customerData="orderData ? orderData.customer : {}"
                :puescData="orderData ? orderData.puesc_information : {}" />
            </d-tab>

            <!-- Historia -->
            <d-tab
              v-if="!userIsWarehouseman && !userIsCustomer"
              :title="$t('order.statusHistory')">
              <order-history
                :statuses="orderData.statuses" />
            </d-tab>

            <!-- Ewidencja -->
            <d-tab
              v-if="!userIsWarehouseman && !userIsCustomer"
              :title="$t('order.evidence')">
              <order-evidence
                :orderID="orderData.id" />
            </d-tab>

            <!-- Powiązane faktury -->
            <d-tab
              v-if="!userIsWarehouseman"
              :title="$t('order.connectedInvoices')">
              <d-row
                v-if="userIsAdmin"
                class="mb-3">
                <d-col cols="12">
                  <label>
                    {{ $t('order.excludeFromInvoiceReminder') }}:
                  </label>

                  <span
                    v-if="orderData.exclude_from_invoice_reminder"
                    class="text-success">
                    {{ $t('ui.yes') }}
                  </span>
                  <span
                    v-else
                    class="text-danger">
                    {{ $t('ui.no') }}
                  </span>
                </d-col>
              </d-row>

              <order-settlements
                :settlementsData="orderData.settlements"
                :orderName="orderData.order_name"
                :orderID="orderData.id"
                :customerID="orderData.customer_id" />
            </d-tab>

            <!-- Wysłane przypomnienia -->
            <d-tab
              v-if="!userIsWarehouseman && !userIsCustomer"
              :title="$t('order.remindersList')">
              <p
                v-if="!orderData.emails.length"
                class="text-center">
                {{ $t('order.noRemindersSent') }}&hellip;
              </p>

              <order-reminders
                v-if="orderData.emails.length"
                :emails="orderData.emails" />
            </d-tab>

            <!-- Kartony -->
            <d-tab
              v-if="userIsWarehouseman || (userIsAdminOrWorker && orderData.boxes_accepted_at)"
              :title="$t('order.warehouse.boxes.title')">
              <order-collect-boxes
                v-if="$showWarehouseBoxesUI"
                :deliveryNoteConfirmedAt="orderData.delivery_note_confirmed_at"
                :deliveryNoteConfirmedBy="orderData.delivery_note_confirmed_by"
                :orderID="orderData.id"
                :orderProducts="orderData.products"
                :boxesAcceptedAt="orderData.boxes_accepted_at"
                :mode="userIsAdminOrWorker ? 'order' : 'warehouse'" />

              <order-returns
                :returns="orderData.embroidery_returns" />
            </d-tab>
          </d-tabs>
        </d-card>
      </validation-observer>
    </d-form>

    <complaint-new-popup
      v-if="userIsAdminOrWorker && showComplaintNewPopup"
      :orderID="parseInt(this.$route.params.id, 10)"
      :products="orderData && orderData.products"
      :services="orderData && orderData.services" />

    <contact-popup
      v-if="showOrderContactPopup"
      :orderID="parseInt(this.$route.params.id, 10)"
      :customerID="orderData.customer_id" />

    <order-erp-integration-popup
      v-if="showErpIntegrationPopup"
      :orderID="parseInt(this.$route.params.id, 10)" />
  </d-container>
</template>

<script>
import ComplaintNewPopup from './../../components/complaints/ComplaintNewPopup.vue';
import Complaints from './../../views/Complaints.vue';
import Contacts from './../../views/Contacts.vue';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import FormUtils from './../../utils/FormUtils.js';
import ContactPopup from './../contacts/ContactPopup.vue';
import OrderCreator from './OrderCreator.vue';
import OrderErpIntegrationPopup from './OrderErpIntegrationPopup.vue';
import OrderEvidence from './OrderEvidence.vue';
import OrderFiles from './OrderFiles.vue';
import OrderHistory from './OrderHistory.vue';
import OrderIEFiles from './OrderIEFiles.vue';
import OrderPuesc from './OrderPuesc.vue';
import OrderReminders from './OrderReminders.vue';
import OrderStatus from './OrderStatus.vue';
import OrderSettlements from './OrderSettlements.vue';
import OrderShipments from './OrderShipments.vue';
import orderStatusPhase from './../../data/order-status-phase.js';
import orderStatuses from './../../data/order-statuses.js';
import OrderCollectBoxes from './OrderCollectBoxes.vue';
import OrderTaxNumberChecker from './OrderTaxNumberChecker.vue';
import OrderReturns from './OrderReturns.vue';
import RequestCreator from './RequestCreator.vue';
import Vue from 'vue';

export default {
  name: 'order-view',
  mixins: [
    CustomerPanel,
    OrderTaxNumberChecker
  ],
  props: [
    'isLocked',
    'orderData'
  ],
  components: {
    'complaint-new-popup': ComplaintNewPopup,
    'order-contacts': Contacts,
    'order-evidence': OrderEvidence,
    'order-ie-files': OrderIEFiles,
    'order-files': OrderFiles,
    'contact-popup': ContactPopup,
    'order-complaints': Complaints,
    'order-creator': OrderCreator,
    'order-erp-integration-popup': OrderErpIntegrationPopup,
    'order-history': OrderHistory,
    'order-puesc': OrderPuesc,
    'order-reminders': OrderReminders,
    'order-shipments': OrderShipments,
    'order-status': OrderStatus,
    'order-settlements': OrderSettlements,
    'order-collect-boxes': OrderCollectBoxes,
    'order-returns': OrderReturns,
    'request-creator': RequestCreator
  },
  computed: {
    allDataLoaded () {
      return this.loadedProducts && this.loadedServices && this.loadedServicesGroups && this.loadedTagCategories && this.loadedCurrencies && this.loadedCountries && this.loadedShippers;
    },
    isIndentList () {
      return this.$route.path.indexOf('/zamowienia-indent') > -1 || this.$route.path.indexOf('/oferty-indent') > -1;
    },
    backUrl () {
      let allowedBackUrls = [
        '/kontakty-z-klientami',
        '/kontenery/rezerwacje',
        '/oferty',
        '/oferty-indent',
        '/reklamacje',
        '/zakupy/rezerwacje',
        '/zamowienia',
        '/zamowienia-indent',
        '/zamowienia-wszystkie',
        '/zapytania',
        '/przesylki'
      ];

      if (this.userIsWarehouseman) {
        return '/zamowienia-wszystkie';
      }

      if (sessionStorage.getItem('headwear-crm-previous-view') && allowedBackUrls.indexOf(sessionStorage.getItem('headwear-crm-previous-view')) > -1) {
        return sessionStorage.getItem('headwear-crm-previous-view');
      }

      if (this.isIndentList) {
        return '/zamowienia-indent';
      }

      return '/zamowienia';
    },
    canDownloadShipperNote () {
      return orderStatusPhase['shipper_note'].indexOf(this.status) > -1;
    },
    canSendSettlementInvoice () {
      return orderStatusPhase['send_invoice'].indexOf(this.status) > -1;
    },
    deadline () {
      let date = this.orderData.deadline;

      if (!date) {
        return this.$t('ui.lack');
      }

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    isOffer () {
      return orderStatusPhase['offer'].indexOf(this.status) > -1;
    },
    realizationDate () {
      let date = this.orderData.realization_date;

      if (!date) {
        return this.$t('ui.lack');
      }

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    shipperDate () {
      let date = this.orderData.shipper_date;

      if (!date) {
        return this.$t('ui.lack');
      }

      return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
    },
    orderStatusText () {
      return orderStatuses[this.$i18n.locale][this.orderData.status];
    },
    addressConfirmedByLabel () {
      if (!this.orderData || !this.orderData.address_confirmed_by) {
        return '';
      }

      return this.orderData.address_confirmed_by_name;
    },
    invoiceRequestInProgress () {
      if (this.orderData) {
        return this.orderData.invoice_request || this.orderData.invoice_request_by_user || this.invoiceRequestSent;
      }

      return this.invoiceRequestSent;
    },
    canResetInvoiceRequest () {
      if (this.orderData) {
        return this.orderData.can_reset_request_invoice;
      }

      return false;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    userIsAdminOrWorker () {
      return this.$store.getters.getUserType === 'admin' || this.$store.getters.getUserType === 'worker';
    },
    userIsWarehouseman () {
      return this.$store.getters.getUserType === 'warehouse_manager';
    },
    invoiceCountryPrefixIsVerified () {
      if (!this.orderData || !this.orderData.customer) {
        return false;
      }

      let countryPrefix = this.orderData.invoice_country_prefix;
      let country = this.countries.filter(countryItem => countryItem.code === countryPrefix);

      if (country && country[0]) {
        return !!country[0].verify_tax_number;
      }

      return false;
    },
    noInvoiceRequests () {
      if (this.orderData) {
        return this.orderData.invoice_request === 0;
      }

      return false;
    }
  },
  watch: {
    allDataLoaded (newValue) {
      if (newValue) {
        this.setUpOrder();
      }
    }
  },
  data() {
    return {
      status: '',
      countries: [],
      currencies: [],
      products: [],
      indentProducts: [],
      services: [],
      servicesGroups: [],
      shippers: [],
      tagCategories: [],
      // editable field
      descriptionToEdit: '',
      warehouseDescriptionToEdit: '',
      editDescription: false,
      editWarehouseDescription: false,
      saveDescriptionInProgress: false,
      saveWarehouseDescriptionInProgress: false,
      // flags
      orderSetUpInProgress: false,
      loadError: false,
      loadedCountries: false,
      loadedCurrencies: false,
      loadedProducts: false,
      loadedIndentProducts: false,
      loadedServices: false,
      loadedServicesGroups: false,
      loadedShippers: false,
      loadedTagCategories: false,
      settlementInvoiceInProgress: false,
      showComplaintNewPopup: false,
      dismissInProgress: false,
      showCollectAlert: false,
      downloadingDeliveryNote: false,
      downloadingPdf: false,
      downloadingXlsx: false,
      showOrderContactPopup: false,
      invoiceCancelRequestSent: false,
      invoiceRequestSent: false,
      taxNumberCheckInProgress: false,
      invoiceResetInProgress: false,
      showEmbroideryAlert: false,
      showErpIntegrationPopup: false,
      excludeFromErpInProgress: false,
      includeToErpInProgress: false,
      unlockInProgress: false,
      // internals
      spinnerTemplate: '<div class="spinner-border spinner-border-sm" role="status"></div>',
      immediateInvoiceRequest: false,
      // temporary data
      shipmentAddressData: {},
      shipmentContactData: {}
    };
  },
  mounted () {
    this.orderSetUpInProgress = true;
    this.status = this.orderData.status;
    this.showCollectAlert = this.orderData && !!this.orderData.collect_alert;
    this.loadAdditionalData();

    this.shipmentAddressData = {
      receiver_name: this.orderData.address_company_name,
      receiver_address_country_code: this.orderData.address_country_id,
      receiver_address_postal_code: this.orderData.address_postcode,
      receiver_address_city: this.orderData.address_city,
      receiver_address_street: this.orderData.address_street,
      receiver_address_building: this.orderData.address_building,
      receiver_address_locale: this.orderData.address_locale,
      receiver_address_line_2: this.orderData.address_line_2,
      receiver_address_line_3: this.orderData.address_line_3
    };
    this.shipmentContactData = {
      receiver_attention_name: this.orderData.address_name,
      receiver_phone: this.orderData.address_phone,
      receiver_email: this.orderData.address_email
    };

    this.$bus.$on('new-order-status', this.changeOrderStatus);
    this.$bus.$on('close-order-contact-popup', this.closeOrderContactPopup);
    this.$bus.$on('show-order-contact-popup', this.openOrderContactPopup);
    this.$bus.$on('show-complaint-add-popup', this.openComplaintAddPopup);
    this.$bus.$on('close-complaint-add-popup', this.closeComplaintAddPopup);
    this.$bus.$on('show-order-embroidery-alert', this.displayEmbroideryAlert);
    this.$bus.$on('order-send-request-shipment-data', this.sendShipmentData);
    this.$bus.$on('show-order-erp-integration-popup', this.openErpIntegrationPopup);
    this.$bus.$on('close-order-erp-integration-popup', this.closeErpIntegrationPopup);
  },
  methods: {
    setUpOrder () {
      this.$refs['order-creator'].setUpProducts(this.orderData.products);
      this.$refs['order-creator'].setUpServices(this.orderData.services, this.orderData.services_packages);

      if (this.$refs['request-creator']) {
        this.$refs['request-creator'].setUpProducts(this.orderData.offer_products);
        this.$refs['request-creator'].setUpServices(this.orderData.offer_services_groups);
      }

      setTimeout(() => {
        this.orderSetUpInProgress = false;
      }, 2000);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/countries/items'),
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/currencies/items'),
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      if (this.isIndent && this.isOffer) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/products/all-items',
          loadedKey: 'loadedProducts',
          outputKey: 'products',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: 0
            }
          }
        });

        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/products/all-items',
          loadedKey: 'loadedIndentProducts',
          outputKey: 'indentProducts',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: 1
            }
          }
        });
      } else {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: this.getApiEndpoint('/products/all-items'),
          loadedKey: 'loadedProducts',
          outputKey: 'products',
          errorKey: 'loadError',
          params: {
            where: {
              is_indent: +this.isIndentList
            }
          }
        });
      }

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/services/all-items'),
        loadedKey: 'loadedServices',
        outputKey: 'services',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: this.getApiEndpoint('/services/groups/all-items'),
        params: {
          with: ['calculatorServices', 'packageServices']
        },
        loadedKey: 'loadedServicesGroups',
        outputKey: 'servicesGroups',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: this.getApiEndpoint('/tags/categories/items'),
        loadedKey: 'loadedTagCategories',
        outputKey: 'tagCategories',
        errorKey: 'loadError',
        params: {
          with: ['tags']
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/shippers/items',
        loadedKey: 'loadedShippers',
        outputKey: 'shippers',
        errorKey: 'loadError'
      });
    },
    changeOrderStatus (newStatus) {
      this.status = newStatus;
    },
    downloadPdf () {
      let orderID = parseInt(this.$route.params.id, 10);
      this.downloadingPdf = orderID;

      this.$http.post('/orders/offer/' + orderID, {
        type: 'pdf'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingPdf = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingPdf = false;
      });
    },
    downloadXlsx () {
      let orderID = parseInt(this.$route.params.id, 10);
      this.downloadingXlsx = orderID;

      this.$http.post('/orders/offer/' + orderID, {
        type: 'xlsx'
      }, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('order.orderFileNamePrefix');

        if (orderStatusPhase['offer-file-prefix'].indexOf(this.status) > -1) {
          filenamePrefix = this.$t('order.offerFileNamePrefix');
        }

        link.setAttribute('download', filenamePrefix + orderID + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingXlsx = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingXlsx = false;
      });
    },
    checkCustomerTaxNumberBeforeInvoice (immediate) {
      let countryPrefix = this.orderData.invoice_country_prefix;
      let customerCountryID = this.orderData.customer ? this.orderData.customer.country_id : 0;
      let taxNumber = this.orderData.invoice_tax_number;
      this.immediateInvoiceRequest = immediate;

      if (this.orderData.transaction_type === 'NO_EU_VAT') {
        this.requestSettlementInvoice();
      } else {
        this.verifyCustomerTaxID(countryPrefix, taxNumber, customerCountryID, this.countries, this.requestSettlementInvoice, false);
      }
    },
    requestSettlementInvoice () {
      this.settlementInvoiceInProgress = true;

      FormUtils.submit({
        $http: this.$http,
        formData: {
          immediately: +this.immediateInvoiceRequest
        },
        endpoint: '/orders/request-invoice/' + parseInt(this.$route.params.id, 10),
        successTitle: this.$t('order.requestSent'),
        successText: this.$t('order.requestForBillingInvoiceSentSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.requestSentErrorText'),
        errorMessages: {
          'INVALID_STATUS': this.$t('order.errors.invalid_status'),
          'INVALID_ADDRESS_COUNTRY_PREFIX': this.$t('order.errors.invalid_address_country_prefix'),
          'INVALID_INVOICE_COUNTRY_PREFIX': this.$t('order.errors.invalid_invoice_country_prefix'),
          'EMPTY_PRICES': this.$t('order.errors.empty_prices'),
          'EMPTY_RECEIVER_EMAIL': this.$t('order.errors.empty_receiver_email'),
          'EMPTY_TRACKING_NUMBER': this.$t('order.errors.empty_tracking_number'),
          'INVOICE_REQUEST': this.$t('order.errors.invoice_request'),
          'INVOICE_EXISTS': this.$t('order.errors.invoice_exists'),
          'NOT_SYNCHRONIZED': this.$t('order.errors.not_synchronized'),
          'PURCHASE_NOT_CLOSED': this.$t('order.errors.purchase_not_closed'),
          'CONTAINER_NOT_CLOSED': this.$t('order.errors.container_not_closed'),
          'EXCLUDE_FROM_ERP_INTEGRATION': this.$t('order.errors.exclude_from_erp_integration'),
          'BLOCK_ERP_INVOICE_REQUEST': this.$t('order.errors.block_erp_invoice_request')
        },
        successAction: () => {
          this.settlementInvoiceInProgress = false;
          this.invoiceRequestSent = true;
        },
        failAction: () => {
          this.settlementInvoiceInProgress = false;
        }
      });
    },
    resetInvoiceRequest () {
      this.invoiceResetInProgress = true;

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/orders/reset-request-invoice/' + parseInt(this.$route.params.id, 10),
        successTitle: this.$t('order.invoiceRequestCancelTitle'),
        successText: this.$t('order.invoiceRequestCancelText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.invoiceRequestCancelError'),
        successAction: () => {
          this.invoiceResetInProgress = false;
          this.orderData.invoice_request = false;
          this.orderData.invoice_request_by_user = false;
          this.invoiceRequestSent = false;
          this.invoiceCancelRequestSent = true;
        },
        failAction: () => {
          this.invoiceResetInProgress = false;
        }
      });
    },
    hasDeadlineWarning () {
      if (this.orderData && this.orderData.warnings && this.orderData.warnings.filter(warning => warning.type === 'DEADLINE').length) {
        return true;
      }

      return false;
    },
    dismissCollectAlert () {
      if (this.dismissInProgress) {
        return;
      }

      this.dismissInProgress = true;
      this.showCollectAlert = false;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/disable-collect-alert/' + this.orderData.id,
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: () => {
          this.dismissInProgress = false;
          this.showCollectAlert = false;
        },
        failAction: () => {
          this.dismissInProgress = false;
          this.showCollectAlert = true;
        }
      });
    },
    downloadDeliveryNote () {
      this.downloadingDeliveryNote = true;

      this.$http.post('/orders/download-order-delivery-note/' + this.orderData.id, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.downloadingDeliveryNote = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingDeliveryNote = false;
      });
    },
    openOrderContactPopup () {
      this.showOrderContactPopup = true;
    },
    closeOrderContactPopup () {
      this.showOrderContactPopup = false;
    },
    unblockOrder () {
      Vue.swal({
        title: this.$t('order.unblockOrderTitle'),
        text: this.$t('order.unblockOrderDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('order.yesUnblock'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.$http.post('/orders/end-edit/' + parseInt(this.orderData.id, 10)).then((results) => {
            if (!results.data || !results.data.success) {
              Vue.swal({
                title: this.$t('ui.errorOccured'),
                html: this.$t('order.unblockError'),
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            } else {
              window.location.reload();
            }
          }).catch(error => {
            console.log(error);
          });
        }
      });
    },
    saveEditedDescription () {
      this.saveDescriptionInProgress = true;
      let descriptionToSave = this.descriptionToEdit;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/update-description/' + parseInt(this.$route.params.id, 10),
        formData: {
          description: descriptionToSave
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.saveChangesErrorText'),
        successAction: () => {
          this.saveDescriptionInProgress = false;
          this.orderData.description = descriptionToSave;
          this.editDescription = false;
        },
        failAction: () => {
          this.saveDescriptionInProgress = false;
        }
      });
    },
    saveEditedWarehouseDescription () {
      this.saveWarehouseDescriptionInProgress = true;
      Vue.set(this.orderData, 'warehouse_description', this.warehouseDescriptionToEdit);

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/update-by-warehouse/' + this.orderData.id,
        formData: {
          warehouse_description: this.orderData.warehouse_description
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: () => {
          this.saveWarehouseDescriptionInProgress = false;
          this.editWarehouseDescription = false;
          Vue.swal({
            title: this.$t('order.commentsSaved'),
            html: '',
            type: 'success',
            timer: 2000,
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        },
        failAction: () => {
          this.saveWarehouseDescriptionInProgress = false;
        }
      });
    },
    toggleDescriptionEdit () {
      this.editDescription = true;
      Vue.set(this, 'descriptionToEdit', this.orderData.description);
    },
    toggleWarehouseDescriptionEdit () {
      this.editWarehouseDescription = true;
      Vue.set(this, 'warehouseDescriptionToEdit', this.orderData.warehouse_description);
    },
    openComplaintAddPopup () {
      this.showComplaintNewPopup = true;
    },
    closeComplaintAddPopup () {
      this.showComplaintNewPopup = false;
    },
    prepareDiscount (value) {
      if (Number.isNaN(value) || (typeof value === 'string' && value.trim() === '')) {
        return 0;
      }

      if (typeof value === 'string') {
        value = parseFloat(value.replace(',', '.'));
      }

      return value;
    },
    contrastColor (colorBg) {
      let threshold = 130;
      let r = parseInt(colorBg.replace('#', '').substring(0, 2), 16);
      let g = parseInt(colorBg.replace('#', '').substring(2, 4), 16);
      let b = parseInt(colorBg.replace('#', '').substring(4, 6), 16);
      let brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      if (brightness > threshold) {
        return '#000';
      }

      return '#fff';
    },
    displayEmbroideryAlert () {
      this.showEmbroideryAlert = true;
    },
    sendShipmentData () {
      let shipperCode = this.orderData.shipper_id;
      let foundedShipper = this.shippers.filter(shipper => shipper.id === this.orderData.shipper_id);

      if (foundedShipper[0]) {
        shipperCode = foundedShipper[0].code;
      }

      this.$bus.$emit('order-receive-shipment-data', {
        'trackingNumber': this.orderData.tracking_number,
        'shipperID': shipperCode,
        'shippers': this.shippers
      });
    },
    closeErpIntegrationPopup () {
      Vue.set(this, 'showErpIntegrationPopup', false);
    },
    openErpIntegrationPopup () {
      Vue.set(this, 'showErpIntegrationPopup', true);
    },
    excludeFromERP () {
      if (this.excludeFromErpInProgress) {
        return;
      }

      this.excludeFromErpInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/exclude-from-erp-integration/' + this.orderData.id,
        formData: {
          exclude_from_erp_integration: 1
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.excludeFromErpInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.erp.excludeFailedTitle'),
              html: this.$t('order.erp.excludeFailedDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.orderData.exclude_from_erp_integration = true;
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.excludeFromErpInProgress = false;
        }
      });
    },
    includeToERP () {
      if (this.includeToErpInProgress) {
        return;
      }

      this.includeToErpInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/exclude-from-erp-integration/' + this.orderData.id,
        formData: {
          exclude_from_erp_integration: 0
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.includeToErpInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.erp.includeFailedTitle'),
              html: this.$t('order.erp.includeFailedDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.orderData.exclude_from_erp_integration = false;
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.includeToErpInProgress = false;
        }
      });
    },
    unlockOrderEdit () {
      if (this.unlockInProgress) {
        return;
      }

      this.unlockInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/orders/change-force-can-update/' + this.orderData.id,
        formData: {
          force_can_update: 1
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        successAction: (response) => {
          this.unlockInProgress = false;

          if (response.success) {
            Vue.swal({
              title: this.$t('order.unlockSuccessTitle'),
              html: this.$t('order.unlockSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            }).then(() => {
              window.location.reload();
            });
          } else {
            Vue.swal({
              title: this.$t('ui.errorOccured'),
              html: this.$t('ui.actionFailedTryAgain'),
              type: 'error',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.unlockInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('close-order-contact-popup', this.closeOrderContactPopup);
    this.$bus.$off('show-order-contact-popup', this.openOrderContactPopup);
    this.$bus.$off('show-complaint-add-popup', this.openComplaintAddPopup);
    this.$bus.$off('close-complaint-add-popup', this.closeComplaintAddPopup);
    this.$bus.$off('show-order-embroidery-alert', this.displayEmbroideryAlert);
    this.$bus.$off('order-send-request-shipment-data', this.sendShipmentData);
    this.$bus.$off('show-order-erp-integration-popup', this.openErpIntegrationPopup);
    this.$bus.$off('close-order-erp-integration-popup', this.closeErpIntegrationPopup);
  }
};
</script>

<style lang="scss" scoped>
.form-control.is-select {
  background-image: none!important;
  padding: 0!important;
}

.order-status-wrapper {
  width: 400px;

  & > div {
    display: flex;
    width: 100%;
  }

  /deep/ .v-select {
    width: 100%;

    .vs__dropdown-toggle {
      border: 1px solid #e1e5eb;
    }
  }
}

.text-muted {
  font-weight: 300;
}

pre {
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  padding: 20px;
  white-space: pre-wrap;
}

.order-tags-list {
  display: flex;
  flex-wrap: wrap;
  list-style-type: none;
  margin: 10px -5px 0 -5px;
  padding: 0 20px 10px 0;

  li {
    font-size: 12px;
    margin: 5px;
  }
}

.has-special-requirements {
  color: #fff!important;
  font-size: 150%;
  margin-bottom: 2rem;
  margin-left: -1.875rem;
  margin-right: -1.875rem;
  margin-top: -1.875rem;
  text-align: center;
  width: calc(100% + (2 * 1.875rem));

  i {
    font-size: 100%;
  }
}

.is-unlock-notice {
  border-bottom: 1px solid #ddd;
  margin-bottom: 2rem;
  margin-left: -1.875rem;
  margin-right: -1.875rem;
  margin-top: -1.875rem;
  text-align: center;
  width: calc(100% + (2 * 1.875rem));
}
</style>
